import React, { useState, useEffect } from 'react';
import { playThemeSound } from '../../../utils/themeAudio';

const CRACKER_PALETTES = [
    ['#f59e0b', '#fbbf24', '#fde047'], // Saffron Gold
    ['#10b981', '#34d399', '#6ee7b7'], // Emerald Green
    ['#f43f5e', '#fb7185', '#fda4af'], // Ruby Crimson
    ['#06b6d4', '#22d3ee', '#67e8f9'], // Vibrant Cyan
    ['#a855f7', '#c084fc', '#e9d5ff'], // Royal Purple
    ['#f97316', '#fb923c', '#fdba74'], // Marigold Orange
];

const CRACKER_TOOLS = [
    { id: 'ladi', label: 'Red Ladi', icon: '🧨', color: '#ef4444' },
    { id: 'skyShot', label: 'Sky Shot', icon: '🚀', color: '#3b82f6' },
    { id: 'chakri', label: 'Chakri', icon: '🌀', color: '#f59e0b' },
    { id: 'anar', label: 'Grand Anar', icon: '🌋', color: '#10b981' }
];

const DiwaliCrackersShow = ({ isMegaDhamaka }) => {
    const [skyBursts, setSkyBursts] = useState([]);
    const [activeLadi, setActiveLadi] = useState([]);
    const [activeChakris, setActiveChakris] = useState([]);
    const [activeAnars, setActiveAnars] = useState([]);

    // Spawn colorful aerial firework burst
    const spawnSkyBurst = (xPercent, yPercent, playAudio = true) => {
        if (playAudio) {
            playThemeSound('diwali', 'skyShot');
        }

        const id = Date.now() + Math.random();
        const palette = CRACKER_PALETTES[Math.floor(Math.random() * CRACKER_PALETTES.length)];
        const count = 28;

        const particles = Array.from({ length: count }).map((_, i) => {
            const angle = (i / count) * 2 * Math.PI;
            const dist = 60 + Math.random() * 85;
            return {
                id: i,
                dx: Math.cos(angle) * dist,
                dy: Math.sin(angle) * dist,
                color: palette[i % palette.length],
                size: 3.5 + Math.random() * 4.5
            };
        });

        setSkyBursts(prev => [...prev, { id, x: xPercent, y: yPercent, particles }]);

        setTimeout(() => {
            setSkyBursts(prev => prev.filter(b => b.id !== id));
        }, 1100);
    };

    // Ambient periodic sky crackers
    useEffect(() => {
        const triggerAmbient = () => {
            const x = 10 + Math.random() * 80;
            const y = 10 + Math.random() * 40;
            spawnSkyBurst(x, y, false);

            const nextTime = 3000 + Math.random() * 3200;
            return setTimeout(() => {
                timer = triggerAmbient();
            }, nextTime);
        };

        let timer = triggerAmbient();
        return () => clearTimeout(timer);
    }, []);

    // Global click: ignite a cracker at click coordinates
    useEffect(() => {
        const handleGlobalClick = (e) => {
            const tagName = e.target.tagName.toLowerCase();
            if (['input', 'textarea', 'button', 'a', 'select'].includes(tagName)) return;
            if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input')) return;

            const xPercent = (e.clientX / window.innerWidth) * 100;
            const yPercent = (e.clientY / window.innerHeight) * 100;

            // Randomly choose between a colorful sky burst or spinning chakri
            if (Math.random() > 0.4) {
                spawnSkyBurst(xPercent, yPercent, true);
            } else {
                igniteChakri(e.clientX, e.clientY);
            }
        };

        window.addEventListener('click', handleGlobalClick, { passive: true });
        return () => window.removeEventListener('click', handleGlobalClick);
    }, []);

    // Ignite Red Ladi
    const igniteLadi = () => {
        playThemeSound('diwali', 'ladi');
        const ladiId = Date.now();
        setActiveLadi(prev => [...prev, { id: ladiId, x: 20 + Math.random() * 60 }]);
        setTimeout(() => {
            setActiveLadi(prev => prev.filter(l => l.id !== ladiId));
        }, 1500);
    };

    // Ignite Sky Shot
    const igniteSkyShot = () => {
        const x = 20 + Math.random() * 60;
        spawnSkyBurst(x, 25 + Math.random() * 20, true);
    };

    // Ignite Spinning Chakri
    const igniteChakri = (clientX = null, clientY = null) => {
        playThemeSound('diwali', 'chakri');
        const cId = Date.now() + Math.random();
        const x = clientX || (window.innerWidth * (0.3 + Math.random() * 0.4));
        const y = clientY || (window.innerHeight - 120);

        setActiveChakris(prev => [...prev, { id: cId, x, y }]);
        setTimeout(() => {
            setActiveChakris(prev => prev.filter(c => c.id !== cId));
        }, 1400);
    };

    // Ignite Anar Fountain
    const igniteAnar = () => {
        playThemeSound('diwali', 'anar');
        const aId = Date.now();
        setActiveAnars(prev => [...prev, { id: aId, x: 30 + Math.random() * 40 }]);
        setTimeout(() => {
            setActiveAnars(prev => prev.filter(a => a.id !== aId));
        }, 2200);
    };

    const handleCrackerTool = (toolId) => {
        switch (toolId) {
            case 'ladi':
                igniteLadi();
                break;
            case 'skyShot':
                igniteSkyShot();
                break;
            case 'chakri':
                igniteChakri();
                break;
            case 'anar':
                igniteAnar();
                break;
            default:
                break;
        }
    };

    return (
        <div className="fixed inset-0 pointer-events-none z-[63] overflow-hidden select-none">
            {/* Crackers Interactive Toolbar in Bottom-Right */}
            <div className="fixed bottom-5 right-4 md:right-8 z-[68] flex items-center gap-1.5 md:gap-2 pointer-events-auto">
                {CRACKER_TOOLS.map((tool) => (
                    <button
                        key={tool.id}
                        type="button"
                        onClick={() => handleCrackerTool(tool.id)}
                        className="px-2.5 py-1.5 rounded-xl bg-gray-950/85 hover:bg-gray-900 border border-amber-500/50 text-amber-300 text-xs font-bold shadow-lg shadow-black/60 transition-all hover:scale-110 active:scale-95 flex items-center gap-1 group"
                        title={`Click to ignite ${tool.label}!`}
                    >
                        <span className="text-base group-hover:animate-bounce">{tool.icon}</span>
                        <span className="hidden sm:inline text-[11px]">{tool.label}</span>
                    </button>
                ))}
            </div>

            {/* Red Ladi Crackle String Animation */}
            {activeLadi.map((ladi) => (
                <div
                    key={ladi.id}
                    className="fixed bottom-12 z-[72] pointer-events-none flex flex-col items-center animate-bounce"
                    style={{ left: `${ladi.x}%` }}
                >
                    <div className="text-2xl filter drop-shadow-[0_0_12px_#ef4444]">
                        🧨 💥 🧨 💥 🧨
                    </div>
                    {/* Smoke & Spark Particles */}
                    <div className="w-12 h-6 bg-red-500/30 blur-md rounded-full animate-ping" />
                </div>
            ))}

            {/* Spinning Ground Chakris */}
            {activeChakris.map((c) => (
                <div
                    key={c.id}
                    className="fixed -translate-x-1/2 -translate-y-1/2 z-[70] pointer-events-none animate-spin text-3xl filter drop-shadow-[0_0_16px_#f59e0b]"
                    style={{ left: c.x, top: c.y, animationDuration: '0.35s' }}
                >
                    🌀 ✨ 🎆 ✨
                </div>
            ))}

            {/* Multi-Colored Anar Fountains */}
            {(activeAnars.length > 0 || isMegaDhamaka) && (
                <div className="fixed bottom-0 inset-x-0 z-[73] pointer-events-none flex justify-around">
                    {/* Left Anar */}
                    <div className="relative flex flex-col items-center">
                        <div className="relative w-4 h-4">
                            {Array.from({ length: 30 }).map((_, i) => {
                                const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.3;
                                const speed = 220 + Math.random() * 260;
                                const dx = Math.cos(angle) * speed;
                                const dy = Math.sin(angle) * speed;
                                const color = ['#fde047', '#10b981', '#f43f5e', '#38bdf8', '#fbbf24'][i % 5];
                                return (
                                    <div
                                        key={i}
                                        className="absolute rounded-full animate-firework"
                                        style={{
                                            width: `${3.5 + Math.random() * 4}px`,
                                            height: `${3.5 + Math.random() * 4}px`,
                                            backgroundColor: color,
                                            boxShadow: `0 0 12px ${color}`,
                                            transform: `translate(${dx}px, ${dy}px)`,
                                            animationDuration: '1.2s',
                                        }}
                                    />
                                );
                            })}
                        </div>
                        <span className="text-2xl">🌋</span>
                    </div>

                    {/* Right Anar */}
                    <div className="relative flex flex-col items-center">
                        <div className="relative w-4 h-4">
                            {Array.from({ length: 30 }).map((_, i) => {
                                const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.3;
                                const speed = 220 + Math.random() * 260;
                                const dx = Math.cos(angle) * speed;
                                const dy = Math.sin(angle) * speed;
                                const color = ['#fbbf24', '#f59e0b', '#ec4899', '#06b6d4', '#ffffff'][i % 5];
                                return (
                                    <div
                                        key={i}
                                        className="absolute rounded-full animate-firework"
                                        style={{
                                            width: `${3.5 + Math.random() * 4}px`,
                                            height: `${3.5 + Math.random() * 4}px`,
                                            backgroundColor: color,
                                            boxShadow: `0 0 12px ${color}`,
                                            transform: `translate(${dx}px, ${dy}px)`,
                                            animationDuration: '1.2s',
                                        }}
                                    />
                                );
                            })}
                        </div>
                        <span className="text-2xl">🌋</span>
                    </div>
                </div>
            )}

            {/* Aerial Sky Bursts */}
            {skyBursts.map((fw) => (
                <div
                    key={fw.id}
                    className="absolute"
                    style={{ left: `${fw.x}%`, top: `${fw.y}%` }}
                >
                    <div className="absolute -left-3 -top-3 w-6 h-6 rounded-full bg-white blur-sm animate-ping" />
                    {fw.particles.map((p) => (
                        <div
                            key={p.id}
                            className="absolute rounded-full animate-firework"
                            style={{
                                width: `${p.size}px`,
                                height: `${p.size}px`,
                                backgroundColor: p.color,
                                boxShadow: `0 0 12px ${p.color}`,
                                transform: `translate(${p.dx}px, ${p.dy}px)`,
                                transition: 'all 0.95s cubic-bezier(0.1, 0.8, 0.3, 1)',
                            }}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
};

export default DiwaliCrackersShow;
